import React, { useState, useEffect } from 'react';
import { actuatorAPI } from '../../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import {
    Activity,
    Server,
    FileText,
    CheckCircle,
    XCircle,
    Cpu,
    HardDrive,
    Clock,
    Search,
    Shield,
    Database,
    Zap,
    List,
    Layers,
    Terminal,
    Download,
    Eye,
    Settings,
    ChevronDown,
    ChevronUp
} from 'lucide-react';

const ActuatorDashboard = ({ onClose }) => {
    const [activeTab, setActiveTab] = useState('overview'); // overview, metrics, loggers, traces, beans, env, advanced
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const tabs = [
        { id: 'overview', label: 'Overview', icon: CheckCircle },
        { id: 'metrics', label: 'Metrics', icon: Server },
        { id: 'loggers', label: 'Loggers', icon: FileText },
        { id: 'logfile', label: 'Server Logs', icon: Terminal },
        { id: 'traces', label: 'HTTP Traces', icon: Activity },
        { id: 'beans', label: 'Beans', icon: Layers },
        { id: 'env', label: 'Environment', icon: Terminal },
        { id: 'advanced', label: 'Advanced', icon: Settings },
    ];

    // Overview Data
    const [health, setHealth] = useState(null);
    const [info, setInfo] = useState(null);

    // Metrics Data
    const [metrics, setMetrics] = useState({});
    const [systemMetrics, setSystemMetrics] = useState({
        uptime: 0,
        cpuUsage: 0,
        memoryUsed: 0,
        memoryMax: 0,
        diskTotal: 0,
        diskFree: 0
    });

    // Log File Data
    const [logFileContent, setLogFileContent] = useState('');
    const [logFilter, setLogFilter] = useState('');


    // Loggers Data
    const [loggers, setLoggers] = useState({});
    const [loggerSearch, setLoggerSearch] = useState('com.jit');
    const [expandedLoggers, setExpandedLoggers] = useState({});

    // Traces Data
    const [traces, setTraces] = useState([]);
    const [expandedTraces, setExpandedTraces] = useState({});

    // Beans Data
    const [beans, setBeans] = useState({});
    const [beansSearch, setBeansSearch] = useState('');

    // Env Data
    const [envProps, setEnvProps] = useState([]);
    const [envSearch, setEnvSearch] = useState('');

    // Load Data based on Tab
    useEffect(() => {
        if (activeTab === 'overview') fetchOverview();
        if (activeTab === 'metrics') fetchMetrics();
        if (activeTab === 'loggers') fetchLoggers();
        if (activeTab === 'traces') fetchTraces();
        if (activeTab === 'beans') fetchBeans();
        if (activeTab === 'env') fetchEnv();
        if (activeTab === 'logfile') fetchLogFile();
    }, [activeTab]);

    // Initial load
    useEffect(() => {
        fetchOverview();
    }, []);

    const fetchOverview = async () => {
        try {
            const healthRes = await actuatorAPI.getHealth();
            setHealth(healthRes.data);
            if (healthRes.data?.components?.diskSpace?.details) {
                const { total, free } = healthRes.data.components.diskSpace.details;
                setSystemMetrics(prev => ({ ...prev, diskTotal: total, diskFree: free }));
            }
            try {
                const infoRes = await actuatorAPI.getInfo();
                setInfo(infoRes.data);
            } catch (e) { }
        } catch (error) { console.error("Failed to fetch health", error); }
    };

    const fetchMetrics = async () => {
        setLoading(true);
        try {
            const [cpuRes, memUsedRes, memMaxRes, uptimeRes] = await Promise.all([
                actuatorAPI.getMetricDetails('system.cpu.usage'),
                actuatorAPI.getMetricDetails('jvm.memory.used'),
                actuatorAPI.getMetricDetails('jvm.memory.max'),
                actuatorAPI.getMetricDetails('process.uptime')
            ]);

            setSystemMetrics(prev => ({
                ...prev,
                cpuUsage: (cpuRes.data.measurements[0].value * 100).toFixed(1),
                memoryUsed: memUsedRes.data.measurements[0].value,
                memoryMax: memMaxRes.data.measurements[0].value,
                uptime: uptimeRes.data.measurements[0].value.toFixed(0)
            }));
        } catch (error) { console.error("Failed to fetch metrics", error); } finally { setLoading(false); }
    };

    const fetchLoggers = async () => {
        setLoading(true);
        try {
            const res = await actuatorAPI.getLoggers();
            setLoggers(res.data.loggers);
        } catch (error) { console.error("Failed to fetch loggers", error); } finally { setLoading(false); }
    };

    const fetchTraces = async () => {
        setLoading(true);
        try {
            const res = await actuatorAPI.getHttpExchanges();
            setTraces(res.data.exchanges || []);
        } catch (error) { console.error("Failed to fetch traces", error); } finally { setLoading(false); }
    };

    const fetchBeans = async () => {
        setLoading(true);
        try {
            const res = await actuatorAPI.getBeans();
            // Flatten context beans for easier display
            const allBeans = {};
            Object.values(res.data.contexts).forEach(ctx => {
                Object.assign(allBeans, ctx.beans);
            });
            setBeans(allBeans);
        } catch (error) { console.error("Failed to fetch beans", error); } finally { setLoading(false); }
    };

    const fetchEnv = async () => {
        setLoading(true);
        try {
            const res = await actuatorAPI.getEnv();
            setEnvProps(res.data.propertySources || []);
        } catch (error) { console.error("Failed to fetch env", error); } finally { setLoading(false); }
    };

    const fetchLogFile = async () => {
        setLoading(true);
        try {
            const res = await actuatorAPI.getLogFile();
            setLogFileContent(res.data || 'No logs found or log file is empty.');
        } catch (error) {
            console.error("Failed to fetch log file", error);
            setLogFileContent("Failed to load logs. Ensure logging.file.name is set in application.properties.");
        } finally { setLoading(false); }
    };

    const handleSetLogLevel = async (name, level) => {
        try {
            await actuatorAPI.setLoggerLevel(name, level);
            // Refresh loggers
            fetchLoggers();
        } catch (error) {
            console.error("Failed to set log level", error);
            alert("Failed to update log level");
        }
    };

    const handleViewLogs = (loggerName) => {
        setLogFilter(loggerName);
        setActiveTab('logfile');
    };

    const handleDownload = async (apiFunc, fileNamePrefix, extension) => {
        setLoading(true);
        try {
            const res = await apiFunc();
            const dateStr = new Date().toISOString().slice(0, 10);
            const fileName = `${fileNamePrefix}-${dateStr}.${extension}`;

            // Create blob link to download
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();
            link.parentNode.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Download failed", error);
            const msg = error.response?.data?.message || error.message || "Failed to download file. Ensure backend is running.";
            alert(`Download failed: ${msg}. For large files, please wait as it may take time.`);
        } finally {
            setLoading(false);
        }
    };

    // Helpers
    const formatUptime = (seconds) => {
        const h = Math.floor(seconds / 3600);
        const m = Math.floor((seconds % 3600) / 60);
        return `${h}h ${m}m`;
    };

    const formatBytes = (bytes) => {
        if (!bytes) return '0 B';
        const k = 1024;
        const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
    };

    const getLevelBadgeColor = (level) => {
        switch (level) {
            case 'ERROR': return 'bg-rose-100 text-rose-800 border-rose-200 dark:bg-rose-900/40 dark:text-rose-400 dark:border-rose-800';
            case 'WARN': return 'bg-amber-100 text-amber-800 border-amber-200 dark:bg-amber-900/40 dark:text-amber-400 dark:border-amber-800';
            case 'INFO': return 'bg-emerald-100 text-emerald-800 border-emerald-200 dark:bg-emerald-900/40 dark:text-emerald-400 dark:border-emerald-800';
            case 'DEBUG': return 'bg-blue-100 text-blue-800 border-blue-200 dark:bg-blue-900/40 dark:text-blue-400 dark:border-blue-800';
            case 'TRACE': return 'bg-violet-100 text-violet-800 border-violet-200 dark:bg-violet-900/40 dark:text-violet-400 dark:border-violet-800';
            case 'OFF': return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
            default: return 'bg-slate-100 text-slate-800 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700';
        }
    };

    const MetricCard = ({ title, value, subvalue, icon: Icon, color, percent }) => (
        <Card className={`overflow-hidden border-${color}-200 dark:border-${color}-900`}>
            <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className={`p-3 rounded-full bg-${color}-100 dark:bg-${color}-900/20 text-${color}-600 dark:text-${color}-400`}>
                        <Icon className="w-6 h-6" />
                    </div>
                    {percent !== undefined && (
                        <span className={`text-sm font-bold text-${color}-600 dark:text-${color}-400`}>{percent}%</span>
                    )}
                </div>
                <div>
                    <p className="text-sm font-medium text-muted-foreground">{title}</p>
                    <h3 className="text-2xl font-bold mt-1 text-slate-800 dark:text-slate-100">{value}</h3>
                    {subvalue && <p className="text-xs text-muted-foreground mt-1">{subvalue}</p>}
                </div>
                {percent !== undefined && (
                    <div className="w-full bg-slate-100 dark:bg-slate-800 h-1.5 mt-4 rounded-full overflow-hidden">
                        <div className={`h-full bg-${color}-500 transition-all duration-500`} style={{ width: `${Math.min(percent, 100)}%` }} />
                    </div>
                )}
            </CardContent>
        </Card>
    );

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-0 md:p-4 animate-in fade-in duration-300">
            <Card className="w-full h-full md:h-[95vh] max-w-[1400px] flex flex-col shadow-2xl bg-white dark:bg-slate-950 border-slate-200 dark:border-slate-800 overflow-hidden ring-1 ring-slate-900/5 rounded-none md:rounded-xl">

                {/* Header */}
                <div className="flex flex-row items-center justify-between border-b px-4 md:px-6 py-4 bg-slate-50/80 dark:bg-slate-900/80 backdrop-blur shrink-0">
                    <div className="flex items-center gap-4">
                        <div className="p-2.5 bg-violet-600 rounded-xl text-white shadow-lg shadow-violet-500/20">
                            <Activity className="h-6 w-6" />
                        </div>
                        <div>
                            <CardTitle className="text-lg md:text-xl font-bold text-slate-900 dark:text-white truncate max-w-[200px] md:max-w-none">
                                System Diagnostics
                            </CardTitle>
                            <p className="text-xs text-muted-foreground mt-0.5 flex items-center gap-2">
                                <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                                Live Monitoring Active
                            </p>
                        </div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={onClose} className="rounded-full hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-900/20 dark:hover:text-rose-400 transition-colors">
                        <XCircle className="h-6 w-6" />
                    </Button>
                </div>

                {/* Navigation Tabs (Desktop) */}
                <div className="hidden md:block px-6 border-b bg-white dark:bg-slate-950 overflow-x-auto no-scrollbar shrink-0">
                    <div className="flex space-x-1 min-w-max py-2">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`
                                    flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all
                                    ${activeTab === tab.id
                                        ? 'bg-violet-50 text-violet-700 dark:bg-violet-900/20 dark:text-violet-300 shadow-sm ring-1 ring-violet-200 dark:ring-violet-800'
                                        : 'text-slate-600 hover:text-slate-900 hover:bg-slate-50 dark:text-slate-400 dark:hover:text-slate-200 dark:hover:bg-slate-900'
                                    }
                                `}
                            >
                                <tab.icon className="w-4 h-4" />
                                {tab.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Navigation (Mobile Dropdown - Custom Styled) */}
                <div className="md:hidden px-4 py-3 border-b bg-white/80 dark:bg-slate-950/80 backdrop-blur-sm sticky top-0 z-40">
                    <div className="relative">
                        <button
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="w-full flex items-center justify-between bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-800 dark:text-slate-100 text-sm font-medium rounded-xl px-4 py-3 focus:ring-2 focus:ring-violet-500/50 outline-none transition-all shadow-sm active:scale-[0.99]"
                        >
                            <div className="flex items-center gap-3">
                                {(() => {
                                    const active = tabs.find(t => t.id === activeTab);
                                    const Icon = active?.icon || Activity;
                                    return <Icon className="w-5 h-5 text-violet-600 dark:text-violet-400" />;
                                })()}
                                <span>{tabs.find(t => t.id === activeTab)?.label}</span>
                            </div>
                            <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform duration-300 ${isMobileMenuOpen ? 'rotate-180' : ''}`} />
                        </button>

                        {/* Dropdown Menu */}
                        {isMobileMenuOpen && (
                            <>
                                <div className="fixed inset-0 z-40 bg-black/20 backdrop-blur-[1px]" onClick={() => setIsMobileMenuOpen(false)} />
                                <div className="absolute top-full left-0 right-0 mt-2 z-50 bg-white dark:bg-slate-900 rounded-xl shadow-xl border border-slate-100 dark:border-slate-800 overflow-hidden animate-in fade-in slide-in-from-top-2 duration-200">
                                    <div className="max-h-[60vh] overflow-y-auto py-2">
                                        {tabs.map(tab => (
                                            <button
                                                key={tab.id}
                                                onClick={() => {
                                                    setActiveTab(tab.id);
                                                    setIsMobileMenuOpen(false);
                                                }}
                                                className={`
                                                    flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors
                                                    ${activeTab === tab.id
                                                        ? 'bg-violet-50 dark:bg-violet-900/20 text-violet-700 dark:text-violet-300 border-l-4 border-violet-500'
                                                        : 'text-slate-600 dark:text-slate-400 hover:bg-slate-50 dark:hover:bg-slate-800/50'
                                                    }
                                                `}
                                            >
                                                <tab.icon className={`w-5 h-5 ${activeTab === tab.id ? 'text-violet-600 dark:text-violet-400' : 'text-slate-400'}`} />
                                                {tab.label}
                                                {activeTab === tab.id && <CheckCircle className="w-4 h-4 ml-auto text-violet-600 dark:text-violet-400" />}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>

                {/* Main Content Area */}
                <div className="flex-1 overflow-y-auto bg-slate-50/50 dark:bg-slate-900/30 p-2 md:p-6 pb-20 md:pb-6">

                    {/* OVERVIEW TAB */}
                    {activeTab === 'overview' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {/* Health Status Block */}
                            <div className={`p-6 rounded-2xl border ${health?.status === 'UP' ? 'bg-gradient-to-br from-emerald-50 to-teal-50 border-emerald-100 dark:from-emerald-950/30 dark:to-teal-950/30 dark:border-emerald-900' : 'bg-rose-50 border-rose-100'} shadow-sm`}>
                                <div className="flex flex-col md:flex-row items-center md:items-start justify-between gap-4 text-center md:text-left">
                                    <div className="flex flex-col md:flex-row items-center gap-4">
                                        <div className={`p-4 rounded-xl ${health?.status === 'UP' ? 'bg-emerald-200/50 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-400' : 'bg-rose-200 text-rose-700'}`}>
                                            <Activity className="w-8 h-8" />
                                        </div>
                                        <div>
                                            <h3 className={`text-2xl font-bold ${health?.status === 'UP' ? 'text-emerald-800 dark:text-emerald-300' : 'text-rose-800'}`}>
                                                System Operational
                                            </h3>
                                            <p className={`text-sm mt-1 ${health?.status === 'UP' ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600'}`}>
                                                All critical services are running within normal parameters.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex flex-col items-center md:items-end w-full md:w-auto mt-2 md:mt-0">
                                        <p className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1">Current Status</p>
                                        <span className={`inline-flex items-center px-4 py-1.5 rounded-full text-sm font-bold shadow-sm ${health?.status === 'UP' ? 'bg-emerald-500 text-white' : 'bg-rose-500 text-white'}`}>
                                            {health?.status || 'UNKNOWN'}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                                <MetricCard
                                    title="Uptime"
                                    value={formatUptime(systemMetrics.uptime)}
                                    icon={Clock}
                                    color="amber"
                                    subvalue="Since last restart"
                                />



                                <MetricCard
                                    title="Memory Usage"
                                    value={formatBytes(systemMetrics.memoryUsed)}
                                    percent={systemMetrics.memoryMax ? (systemMetrics.memoryUsed / systemMetrics.memoryMax * 100) : 0}
                                    icon={Zap}
                                    color="purple"
                                    subvalue={`of ${formatBytes(systemMetrics.memoryMax)} Total`}
                                />
                                <MetricCard
                                    title="Disk Usage"
                                    value={formatBytes(systemMetrics.diskTotal - systemMetrics.diskFree)}
                                    percent={systemMetrics.diskTotal ? ((systemMetrics.diskTotal - systemMetrics.diskFree) / systemMetrics.diskTotal * 100) : 0}
                                    icon={HardDrive}
                                    color="blue"
                                    subvalue={`of ${formatBytes(systemMetrics.diskTotal)} Total`}
                                />
                                <MetricCard
                                    title="CPU Load"
                                    value={`${systemMetrics.cpuUsage}%`}
                                    icon={Cpu}
                                    color="rose"
                                    subvalue="Current Load"
                                />
                            </div>
                        </div>
                    )}

                    {/* METRICS TAB */}
                    {activeTab === 'metrics' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                {/* Detailed Memory */}
                                <Card className="border-purple-100 dark:border-purple-900/50">
                                    <CardHeader>
                                        <CardTitle className="text-purple-700 dark:text-purple-400 flex items-center gap-2">
                                            <Zap className="w-5 h-5" /> JVM Memory Details
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">{formatBytes(systemMetrics.memoryUsed)}</p>
                                                <p className="text-xs md:text-sm text-muted-foreground">Used Memory</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">{formatBytes(systemMetrics.memoryMax)}</p>
                                                <p className="text-xs md:text-sm text-muted-foreground">Total Allocated</p>
                                            </div>
                                        </div>
                                        <div className="h-4 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                            <div className="h-full bg-purple-500 transition-all duration-700" style={{ width: `${(systemMetrics.memoryUsed / systemMetrics.memoryMax) * 100}%` }} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 bg-purple-50 dark:bg-purple-900/20 rounded-xl">
                                                <span className="text-xs font-semibold text-purple-600 block mb-1">FREE MEMORY</span>
                                                <span className="text-lg font-mono text-purple-800 dark:text-purple-300">{formatBytes(systemMetrics.memoryMax - systemMetrics.memoryUsed)}</span>
                                            </div>
                                            <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                                <span className="text-xs font-semibold text-slate-500 block mb-1">USAGE %</span>
                                                <span className="text-lg font-mono text-slate-700 dark:text-slate-300">{((systemMetrics.memoryUsed / systemMetrics.memoryMax) * 100).toFixed(1)}%</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>

                                {/* Detailed Disk */}
                                <Card className="border-blue-100 dark:border-blue-900/50">
                                    <CardHeader>
                                        <CardTitle className="text-blue-700 dark:text-blue-400 flex items-center gap-2">
                                            <HardDrive className="w-5 h-5" /> Disk Space Details
                                        </CardTitle>
                                    </CardHeader>
                                    <CardContent className="space-y-6">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <p className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">{formatBytes(systemMetrics.diskTotal - systemMetrics.diskFree)}</p>
                                                <p className="text-xs md:text-sm text-muted-foreground">Used Space</p>
                                            </div>
                                            <div className="text-right">
                                                <p className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-slate-100">{formatBytes(systemMetrics.diskTotal)}</p>
                                                <p className="text-xs md:text-sm text-muted-foreground">Total Capacity</p>
                                            </div>
                                        </div>
                                        <div className="h-4 w-full bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                                            <div className="h-full bg-blue-500 transition-all duration-700" style={{ width: `${((systemMetrics.diskTotal - systemMetrics.diskFree) / systemMetrics.diskTotal * 100)}%` }} />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl">
                                                <span className="text-xs font-semibold text-blue-600 block mb-1">FREE SPACE</span>
                                                <span className="text-lg font-mono text-blue-800 dark:text-blue-300">{formatBytes(systemMetrics.diskFree)}</span>
                                            </div>
                                            <div className="p-4 bg-slate-50 dark:bg-slate-900/50 rounded-xl">
                                                <span className="text-xs font-semibold text-slate-500 block mb-1">USAGE %</span>
                                                <span className="text-lg font-mono text-slate-700 dark:text-slate-300">{((systemMetrics.diskTotal - systemMetrics.diskFree) / systemMetrics.diskTotal * 100).toFixed(1)}%</span>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            </div>
                        </div>
                    )}

                    {/* LOGGERS TAB */}
                    {activeTab === 'loggers' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border shadow-sm flex flex-col md:flex-row gap-4 items-center justify-between">
                                <div className="relative w-full md:w-96">
                                    <Search className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
                                    <input
                                        type="text"
                                        placeholder="Search loggers (e.g. com.jit)..."
                                        className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-slate-900 border-none rounded-lg focus:ring-2 focus:ring-violet-500 outline-none"
                                        value={loggerSearch}
                                        onChange={(e) => setLoggerSearch(e.target.value)}
                                    />
                                </div>
                                <div className="flex gap-2 text-sm text-muted-foreground">
                                    <span>Total Loggers: <span className="font-bold text-slate-900 dark:text-white">{Object.keys(loggers).length}</span></span>
                                </div>
                            </div>

                            <div className="bg-white dark:bg-slate-950 rounded-xl border shadow-sm overflow-hidden">
                                {/* Desktop View */}
                                <div className="hidden md:block overflow-x-auto">
                                    <table className="w-full text-sm text-left">
                                        <thead className="bg-slate-50 dark:bg-slate-900/50 text-slate-500 font-medium">
                                            <tr>
                                                <th className="px-6 py-4">Logger Name</th>
                                                <th className="px-6 py-4">Current Level</th>
                                                <th className="px-6 py-4">Action</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {Object.entries(loggers)
                                                .filter(([name]) => name.toLowerCase().includes(loggerSearch.toLowerCase()))
                                                .slice(0, 50)
                                                .map(([name, config]) => (
                                                    <tr key={name} className="hover:bg-slate-50 dark:hover:bg-slate-900/30 transition-colors">
                                                        <td className="px-6 py-4 font-mono text-xs text-slate-600 dark:text-slate-400 break-all max-w-md">
                                                            {name}
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <span className={`px-2.5 py-1 rounded-md text-xs font-bold border ${getLevelBadgeColor(config.effectiveLevel)}`}>
                                                                {config.effectiveLevel}
                                                            </span>
                                                        </td>
                                                        <td className="px-6 py-4">
                                                            <div className="flex items-center gap-1">
                                                                {/* View Logs Button (New) */}
                                                                <button
                                                                    onClick={() => handleViewLogs(name)}
                                                                    className="px-2 py-1 mr-2 text-[10px] font-bold rounded border bg-slate-100 text-slate-700 hover:bg-slate-200 border-slate-200 transition-colors flex items-center gap-1"
                                                                >
                                                                    <FileText className="w-3 h-3" /> Logs
                                                                </button>

                                                                {['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'OFF'].map(level => (
                                                                    <button
                                                                        key={level}
                                                                        onClick={() => handleSetLogLevel(name, level)}
                                                                        className={`px-2 py-1 text-[10px] font-bold rounded border transition-all ${config.effectiveLevel === level ? 'bg-slate-800 text-white border-slate-900 shadow' : 'bg-transparent text-slate-500 border-transparent hover:bg-slate-100'}`}
                                                                    >
                                                                        {level.charAt(0)}
                                                                    </button>
                                                                ))}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                ))}
                                        </tbody>
                                    </table>
                                </div>

                                {/* Mobile View (Cards) */}
                                <div className="md:hidden divide-y divide-slate-100 dark:divide-slate-800">
                                    {Object.entries(loggers)
                                        .filter(([name]) => name.toLowerCase().includes(loggerSearch.toLowerCase()))
                                        .slice(0, 50)
                                        .map(([name, config]) => (
                                            <div key={name} className="p-4 space-y-3">
                                                <div className="flex justify-between items-start">
                                                    <div className="font-mono text-xs text-slate-700 dark:text-slate-300 break-all pr-2 font-bold">
                                                        {name}
                                                    </div>
                                                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold border shrink-0 ${getLevelBadgeColor(config.effectiveLevel)}`}>
                                                        {config.effectiveLevel}
                                                    </span>
                                                </div>

                                                <div className="flex flex-wrap gap-2 items-center pt-2 border-t border-slate-50 dark:border-slate-800">
                                                    <button
                                                        onClick={() => handleViewLogs(name)}
                                                        className="px-3 py-1.5 text-xs font-bold rounded-md bg-slate-100 text-slate-700 border border-slate-200 flex items-center gap-1.5 w-full justify-center sm:w-auto"
                                                    >
                                                        <FileText className="w-3 h-3" /> View Logs
                                                    </button>

                                                    <div className="flex flex-wrap gap-1 w-full sm:w-auto justify-center">
                                                        {['TRACE', 'DEBUG', 'INFO', 'WARN', 'ERROR', 'OFF'].map(level => (
                                                            <button
                                                                key={level}
                                                                onClick={() => handleSetLogLevel(name, level)}
                                                                className={`px-2.5 py-1.5 text-[10px] font-bold rounded border transition-all flex-1 sm:flex-none text-center ${config.effectiveLevel === level ? 'bg-slate-800 text-white border-slate-900 shadow' : 'bg-transparent text-slate-500 border-slate-200 hover:bg-slate-50'}`}
                                                            >
                                                                {level}
                                                            </button>
                                                        ))}
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                                {Object.entries(loggers).filter(([name]) => name.toLowerCase().includes(loggerSearch.toLowerCase())).length === 0 && (
                                    <div className="p-12 text-center text-muted-foreground">No loggers found matching your search.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {/* LOGFILE TAB */}
                    {activeTab === 'logfile' && (
                        <div className="h-full flex flex-col animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="flex flex-col md:flex-row items-start md:items-center justify-between mb-2 gap-3">
                                <h3 className="text-sm font-semibold text-slate-700 dark:text-slate-300 flex items-center gap-2">
                                    <Terminal className="w-4 h-4" /> Live Server Logs
                                </h3>
                                <div className="flex flex-wrap gap-2 w-full md:w-auto">
                                    {/* Filter Input */}
                                    {logFilter && (
                                        <div className="flex items-center gap-2 bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 px-3 py-1 rounded-full text-xs font-medium">
                                            <span>Filter: {logFilter}</span>
                                            <button onClick={() => setLogFilter('')} className="hover:text-violet-900 dark:hover:text-white"><XCircle className="w-4 h-4" /></button>
                                        </div>
                                    )}
                                    <input
                                        type="text"
                                        placeholder="Filter logs..."
                                        value={logFilter}
                                        onChange={(e) => setLogFilter(e.target.value)}
                                        className="h-9 text-xs px-3 rounded-lg border bg-white dark:bg-slate-900 outline-none w-full md:w-48 focus:ring-2 focus:ring-violet-500"
                                    />
                                    <Button variant="outline" size="sm" onClick={fetchLogFile} className="h-9 text-xs px-4 w-full md:w-auto">
                                        Refresh Logs
                                    </Button>
                                </div>
                            </div>
                            <div className="flex-1 bg-slate-950 rounded-xl p-4 overflow-auto font-mono text-xs text-slate-200 border border-slate-800 shadow-inner">
                                <pre className="whitespace-pre-wrap break-all">
                                    {logFileContent
                                        ? (logFilter ? logFileContent.split('\n').filter(line => line.toLowerCase().includes(logFilter.toLowerCase())).join('\n') : logFileContent)
                                        : "Loading or No Logs Found..."}
                                    {logFileContent && logFilter && !logFileContent.split('\n').some(line => line.toLowerCase().includes(logFilter.toLowerCase())) && (
                                        <div className="text-slate-500 mt-4 italic text-center">No logs found matching filter "{logFilter}"</div>
                                    )}
                                </pre>
                            </div>
                        </div>
                    )}

                    {/* TRACES TAB */}
                    {activeTab === 'traces' && (
                        <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            {traces.length === 0 ? (
                                <div className="text-center p-12 bg-white dark:bg-slate-950 rounded-xl border border-dashed">
                                    <Activity className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                                    <h3 className="text-lg font-medium text-slate-900 dark:text-white">No Traces Recorded</h3>
                                    <p className="text-sm text-muted-foreground mt-1">HTTP requests will appear here as they occur.</p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {traces.slice().reverse().map((trace, idx) => (
                                        <div key={idx} className="bg-white dark:bg-slate-950 rounded-xl border shadow-sm overflow-hidden transition-all">
                                            <div
                                                className="p-4 flex flex-col md:flex-row gap-4 md:items-center cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-900/30"
                                                onClick={() => setExpandedTraces(prev => ({ ...prev, [idx]: !prev[idx] }))}
                                            >
                                                <div className="flex items-center gap-3 w-full md:w-auto min-w-[120px]">
                                                    <span className={`px-2.5 py-1 rounded-md text-xs font-bold ${trace.response.status >= 500 ? 'bg-rose-100 text-rose-800' : trace.response.status >= 400 ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800'}`}>
                                                        {trace.response.status}
                                                    </span>
                                                    <span className="font-mono text-xs font-bold text-slate-500">{trace.request.method}</span>
                                                </div>
                                                <div className="flex-1 min-w-0">
                                                    <p className="text-sm font-medium text-slate-800 dark:text-slate-200 truncate" title={trace.request.uri}>
                                                        {trace.request.uri}
                                                    </p>
                                                    <p className="text-xs text-muted-foreground mt-0.5">
                                                        {new Date(trace.timestamp).toLocaleTimeString()} • {trace.timeTaken}ms
                                                    </p>
                                                </div>
                                                <div className="text-slate-400">
                                                    {expandedTraces[idx] ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                                                </div>
                                            </div>

                                            {/* Expanded Details */}
                                            {expandedTraces[idx] && (
                                                <div className="bg-slate-50 dark:bg-slate-900/50 p-4 border-t text-xs font-mono grid grid-cols-1 md:grid-cols-2 gap-6 animate-in slide-in-from-top-2">
                                                    <div>
                                                        <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-2 border-b pb-1">Request Headers</h4>
                                                        <div className="space-y-1 text-slate-600 dark:text-slate-400">
                                                            {Object.entries(trace.request.headers || {}).map(([key, val]) => (
                                                                <div key={key} className="break-all">
                                                                    <span className="font-semibold text-slate-500">{key}:</span> {val}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                    <div>
                                                        <h4 className="font-bold text-slate-700 dark:text-slate-300 mb-2 border-b pb-1">Response Headers</h4>
                                                        <div className="space-y-1 text-slate-600 dark:text-slate-400">
                                                            {Object.entries(trace.response.headers || {}).map(([key, val]) => (
                                                                <div key={key} className="break-all">
                                                                    <span className="font-semibold text-slate-500">{key}:</span> {val}
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* BEANS TAB */}
                    {activeTab === 'beans' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border shadow-sm flex items-center gap-4">
                                <Search className="w-4 h-4 text-muted-foreground" />
                                <input
                                    type="text"
                                    placeholder="Search Spring Beans..."
                                    className="w-full bg-transparent outline-none text-sm"
                                    value={beansSearch}
                                    onChange={(e) => setBeansSearch(e.target.value)}
                                />
                            </div>
                            <div className="grid gap-3">
                                {Object.entries(beans)
                                    .filter(([name]) => name.toLowerCase().includes(beansSearch.toLowerCase()))
                                    .slice(0, 50)
                                    .map(([name, detail]) => (
                                        <div key={name} className="bg-white dark:bg-slate-950 p-3 md:p-4 rounded-xl border shadow-sm">
                                            <h4 className="font-mono text-sm font-bold text-slate-800 dark:text-slate-200 break-words">{name}</h4>
                                            <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2 text-xs text-muted-foreground">
                                                <div><span className="font-semibold">Type:</span> <span className="break-all">{detail.type}</span></div>
                                                <div><span className="font-semibold">Scope:</span> {detail.scope}</div>
                                            </div>
                                            {detail.resource && (
                                                <p className="mt-2 text-xs text-slate-500 break-all border-t pt-2 max-h-20 overflow-hidden text-ellipsis"><span className="font-semibold">Resource:</span> {detail.resource}</p>
                                            )}
                                        </div>
                                    ))}
                            </div>
                        </div>
                    )}

                    {/* ENV TAB */}
                    {activeTab === 'env' && (
                        <div className="space-y-4 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <div className="bg-white dark:bg-slate-950 p-4 rounded-xl border shadow-sm">
                                <input
                                    type="text"
                                    placeholder="Search Environment Properties..."
                                    className="w-full bg-transparent outline-none text-sm"
                                    value={envSearch}
                                    onChange={(e) => setEnvSearch(e.target.value)}
                                />
                            </div>

                            {envProps.map((source, idx) => (
                                <div key={idx} className="bg-white dark:bg-slate-950 rounded-xl border shadow-sm overflow-hidden">
                                    <div className="px-6 py-4 bg-slate-50 dark:bg-slate-900/50 border-b flex justify-between items-center cursor-pointer" onClick={() => setExpandedLoggers(prev => ({ ...prev, [source.name]: !prev[source.name] }))}>
                                        <h3 className="font-medium text-sm text-slate-900 dark:text-slate-100">{source.name}</h3>
                                        <span className="py-0.5 px-2 rounded-full bg-slate-200 dark:bg-slate-800 text-xs font-mono">{Object.keys(source.properties || {}).length} props</span>
                                    </div>

                                    {(!expandedLoggers[source.name] || true) && source.properties && (
                                        <div className="divide-y divide-slate-100 dark:divide-slate-800">
                                            {Object.entries(source.properties)
                                                .filter(([key]) => key.toLowerCase().includes(envSearch.toLowerCase()))
                                                .slice(0, 50)
                                                .map(([key, val]) => (
                                                    <div key={key} className="px-4 md:px-6 py-3 hover:bg-slate-50 dark:hover:bg-slate-900/30 flex flex-col md:grid md:grid-cols-3 gap-1 md:gap-2 text-xs md:text-sm transition-colors">
                                                        <span className="font-mono text-slate-600 dark:text-slate-400 break-words font-semibold">{key}</span>
                                                        <span className="font-mono text-slate-900 dark:text-slate-200 md:col-span-2 break-all bg-slate-100 dark:bg-slate-900 p-2 rounded border border-slate-200 dark:border-slate-800">{val.value}</span>
                                                    </div>
                                                ))}
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ADVANCED TAB */}
                    {activeTab === 'advanced' && (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
                            <Card className="hover:shadow-lg transition-shadow border-rose-100 dark:border-rose-900/40">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2 text-rose-700">
                                        <Download className="w-5 h-5" /> Heap Dump
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Download a snapshot of the JVM's heap memory. Useful for analyzing memory leaks.
                                        <br /><span className="font-bold text-rose-600">Warning: File size can be very large.</span>
                                    </p>
                                    <Button onClick={() => handleDownload(actuatorAPI.downloadHeapDump, 'jit-heapdump', 'hprof')} className="w-full bg-rose-600 hover:bg-rose-700 text-white shadow-lg shadow-rose-500/20">
                                        Download Heap Dump (.hprof)
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card className="hover:shadow-lg transition-shadow border-amber-100 dark:border-amber-900/40">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2 text-amber-700">
                                        <List className="w-5 h-5" /> Thread Dump
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        Download a snapshot of all active threads. Useful for diagnosing deadlocks and CPU spikes.
                                    </p>
                                    <Button onClick={() => handleDownload(actuatorAPI.downloadThreadDump, 'jit-threaddump', 'txt')} className="w-full bg-amber-600 hover:bg-amber-700 text-white shadow-lg shadow-amber-500/20">
                                        Download Thread Dump
                                    </Button>
                                </CardContent>
                            </Card>

                            <Card className="hover:shadow-lg transition-shadow md:col-span-2">
                                <CardHeader>
                                    <CardTitle className="text-lg flex items-center gap-2 text-slate-700">
                                        <Settings className="w-5 h-5" /> Configuration Properties
                                    </CardTitle>
                                </CardHeader>
                                <CardContent>
                                    <p className="text-sm text-muted-foreground mb-4">
                                        View the complete hierarchy of configuration properties (`@ConfigurationProperties`) beans.
                                    </p>
                                    <Button variant="outline" className="w-full" onClick={() => window.open('http://localhost:8083/actuator/configprops', '_blank')}>
                                        Open Config Props JSON
                                    </Button>
                                </CardContent>
                            </Card>
                        </div>
                    )}

                </div>
            </Card>
        </div>
    );
};

export default ActuatorDashboard;
